import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWakeLock } from '../hooks/useWakeLock';
import { useAppContext } from '../context/AppContext';
import { Task } from 'shared/types';
import { ImageService } from '../services/imageService';
import { updateChecklistState } from '../services/apiService';
import { i18n } from '../i18n/i18n';
import { useLanguage } from '../context/LanguageContext';
import { getToolDisplayDescription, getToolDisplayName } from '../utils/toolLabels';
import AntMascot from './AntMascot';
import { KidButton, PictureTile } from './kid/KidUI';
import { Star } from './kid/KidIcon';
import { nextStepIndex, previousStepIndex, stepToShow } from './kid/taskFlow';
import { humanizeKeyLabel, toolGridColumns } from './kid/kidUiHelpers';
import { useViewportWidth } from '../hooks/useViewportWidth';
import { useSheet } from '../hooks/useSheet';
import StepTimer from './kid/StepTimer';

/**
 * A member's task, from details to celebration (kid-first redesign, step 2 —
 * docs/design/kid-redesign.md and mock-ups phone-03…06 / tablet-*-03…05).
 *
 * Phases: view (details sheet) → tools_gate → doing (one step per screen) → final (tidy up)
 * → celebrate. "Let's go" opens straight at tools_gate (`startInFlow`); the details sheet is one
 * tap away via "See details". The flow screens are full screen without the navigation bar.
 */
type Phase = 'view' | 'tools_gate' | 'doing' | 'final' | 'celebrate';

export interface TaskMemberModalProps {
  task: Task | null;
  open: boolean;
  onClose: () => void;
  /** "Let's go": skip the details sheet and start at the tools check. */
  startInFlow?: boolean;
}

function resolveChecklistImageUrl(imageId: string): string | undefined {
  if (/^https?:\/\//i.test(imageId)) return imageId;
  return undefined;
}

const TaskMemberModal: React.FC<TaskMemberModalProps> = ({ task, open, onClose, startInFlow }) => {
  const { current_user, current_family, refreshFamilyData, submitTaskForVerification, claimTask, unclaimTask } = useAppContext();
  const { current_language } = useLanguage();
  const navigate = useNavigate();
  const viewportWidth = useViewportWidth();

  const [phase, setPhase] = useState<Phase>('view');
  // Screen stays on while the child works through the task (get ready → steps → tidy up).
  useWakeLock(open && (phase === 'tools_gate' || phase === 'doing' || phase === 'final'));
  const [headerSrc, setHeaderSrc] = useState<string | undefined>();
  const [toolUrls, setToolUrls] = useState<Record<string, string>>({});
  const [checklistUrls, setChecklistUrls] = useState<Record<string, string[]>>({});
  const [completeBusy, setCompleteBusy] = useState(false);
  const [flowError, setFlowError] = useState<string | null>(null);
  // Steps are saved in the background so the child never waits on "Loading..." between steps.
  const [doneLocal, setDoneLocal] = useState<Set<string>>(new Set());
  // Steps un-done by going back (2026-10-13): they count as not done even before the server confirms.
  const [undoneLocal, setUndoneLocal] = useState<Set<string>>(new Set());
  const [viewIndex, setViewIndex] = useState<number | null>(null);
  const pendingSaves = useRef<Promise<void>[]>([]);
  // The task and its stars at the moment of completion, so the celebration never depends on
  // (or flashes during) the family reload that follows.
  const [celebrated, setCelebrated] = useState<{ title: string; points: number; approved?: boolean } | null>(null);

  const liveTask = useMemo(() => {
    if (!task || !current_family?.tasks) return task;
    const t = current_family.tasks.find((x: any) => x.id === task.id);
    return (t as Task) || task;
  }, [task, current_family?.tasks]);

  const isMine = !!(current_user && liveTask && (liveTask as any).assigned_to === current_user.id);
  const isCompleted = !!(liveTask as any)?.is_completed;
  const checklist = useMemo(() => liveTask?.checklist || [], [liveTask?.checklist]);
  const hasChecklist = checklist.length > 0;
  const serverState = useMemo(() => (liveTask as any)?.checklist_state || {}, [liveTask]);
  const isDone = useCallback(
    (id: string) => doneLocal.has(id) || (!!serverState[id] && !undoneLocal.has(id)),
    [serverState, doneLocal, undoneLocal],
  );
  const doneFlags = checklist.map((item) => isDone(item.id));
  const allStepsDone = hasChecklist && doneFlags.every(Boolean);

  // Reset whenever a (different) task is opened.
  useEffect(() => {
    if (!open) return;
    setFlowError(null);
    setDoneLocal(new Set());
    setUndoneLocal(new Set());
    setViewIndex(null);
    setCelebrated(null);
    pendingSaves.current = [];
    const canStart = !!(startInFlow && task && current_user && (task as any).assigned_to === current_user.id
      && !(task as any).is_completed && (task.checklist || []).length > 0);
    setPhase(canStart ? 'tools_gate' : 'view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.id]);

  const checklistKey = useMemo(() => checklist.map((i) => i.id).join(','), [checklist]);

  useEffect(() => {
    if (!open || !liveTask || !current_family) return;

    const load = async () => {
      const t = liveTask as any;
      const items = t.checklist || [];
      let hdr: string | undefined = t.header_image_url;
      if (!hdr && t.header_image) {
        try {
          hdr = (await ImageService.getImageURL(t.header_image)) || undefined;
        } catch {
          hdr = undefined;
        }
      }
      setHeaderSrc(hdr);

      const tu: Record<string, string> = {};
      // Task tools plus any tool a step asks for (2026-10-13).
      const allToolIds = Array.from(new Set([...((t.tools || []) as string[]), ...items.flatMap((it: any) => it.tool_ids || [])]));
      for (const toolId of allToolIds) {
        const toolObj = current_family.tools.find((x: any) => x.id === toolId);
        if (!toolObj) continue;
        if (toolObj.image_url) {
          tu[toolId] = toolObj.image_url;
        } else if (toolObj.image) {
          try {
            const u = await ImageService.getImageURL(toolObj.image);
            if (u) tu[toolId] = u;
          } catch {
            /* ignore — the tile shows its fallback letter */
          }
        }
      }
      setToolUrls(tu);

      const cu: Record<string, string[]> = {};
      for (const item of items) {
        const urls: string[] = [];
        for (const imageId of item.images || []) {
          const direct = resolveChecklistImageUrl(imageId);
          if (direct) {
            urls.push(direct);
            continue;
          }
          try {
            const u = await ImageService.getImageURL(imageId);
            if (u) urls.push(u);
          } catch {
            /* ignore */
          }
        }
        cu[item.id] = urls;
      }
      setChecklistUrls(cu);
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, liveTask?.id, current_family?.id, checklistKey]);

  const getRoomName = useCallback(
    (roomId: string | null) => {
      if (!roomId || !current_family) return '—';
      return current_family.rooms.find((r: any) => r.id === roomId)?.name || '—';
    },
    [current_family],
  );

  const getToolLabel = (tool: any) => humanizeKeyLabel(getToolDisplayName(tool, current_language));
  const getToolDesc = (tool: any) => getToolDisplayDescription(tool, current_language);

  // ── flow actions ──
  const startSteps = () => {
    setFlowError(null);
    const idx = stepToShow(doneFlags, null);
    if (idx >= checklist.length) {
      setPhase('final');
      return;
    }
    setViewIndex(idx);
    setPhase('doing');
  };

  const handleStartTask = () => {
    if (!isMine || isCompleted || !hasChecklist) return;
    if (allStepsDone) setPhase('final');
    else setPhase('tools_gate');
  };

  const handleDidIt = (index: number) => {
    const item = checklist[index];
    if (!item || !liveTask) return;
    setFlowError(null);
    if (!isDone(item.id)) {
      setDoneLocal((prev) => new Set(prev).add(item.id));
      setUndoneLocal((prev) => { const n = new Set(prev); n.delete(item.id); return n; });
      const save = updateChecklistState(liveTask.id, item.id, true)
        .then(() => undefined)
        .catch((e: any) => {
          // Put the step back so the child can try again.
          setDoneLocal((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
          setViewIndex(index);
          setPhase('doing');
          setFlowError(e?.message || 'Failed to save step');
        });
      pendingSaves.current.push(save);
    }
    // The current step counts as done even before the state update lands.
    const next = nextStepIndex(doneFlags.map((d, i) => d || i === index), index);
    if (next >= checklist.length) setPhase('final');
    else setViewIndex(next);
  };

  /** Going back through the steps makes the step you return to "not done" again (2026-10-13, Jens):
   *  a task must never remember old clicks. Saved in the background like "I did it". */
  const unmarkStep = (index: number) => {
    const item = checklist[index];
    if (!item || !liveTask || !isDone(item.id)) return;
    setDoneLocal((prev) => { const n = new Set(prev); n.delete(item.id); return n; });
    setUndoneLocal((prev) => new Set(prev).add(item.id));
    const save = updateChecklistState(liveTask.id, item.id, false)
      .then(() => undefined)
      .catch((e: any) => setFlowError(e?.message || 'Failed to save step'));
    pendingSaves.current.push(save);
  };

  const handleBack = (index: number) => {
    const target = previousStepIndex(index);
    unmarkStep(target);
    setViewIndex(target);
  };

  const handleCompleteTask = async () => {
    if (!liveTask || completeBusy) return;
    setCompleteBusy(true);
    setFlowError(null);
    try {
      await Promise.all(pendingSaves.current);
      pendingSaves.current = [];
      const snapshot = { title: liveTask.title, points: liveTask.points };
      const res = await submitTaskForVerification(liveTask.id, '');
      setCelebrated({ ...snapshot, approved: res?.auto_approved === true });
      setPhase('celebrate');
    } catch (e: any) {
      setFlowError(e?.message || 'Failed to complete task');
    } finally {
      setCompleteBusy(false);
    }
  };

  // Steps were saved in the background, so refresh once when leaving the flow.
  const closeFlow = () => {
    onClose();
    void refreshFamilyData();
  };

  const leaveToHome = () => {
    onClose();
    void refreshFamilyData();
    navigate('/home');
    window.scrollTo({ top: 0 });
  };

  // Escape closes, and the page behind stays still while the flow is open (step 8).
  useSheet(open && !!task, closeFlow);

  if (!open || !task || !liveTask || !current_user || !current_family) return null;

  const taskRoom = (current_family.rooms as any[]).find((r) => r.id === (liveTask as any).room_id) as { name: string; image_url?: string | null } | undefined;
  const toolsList = ((liveTask as any).tools || []) as string[];
  const tools = toolsList.map((id) => current_family.tools.find((t: any) => t.id === id)).filter(Boolean) as any[];
  const hasTools = tools.length > 0;
  const assigneeName = (liveTask as any).assigned_to
    ? current_family.members.find((m: any) => m.id === (liveTask as any).assigned_to)?.name
    : null;

  // ── shared flow chrome ──
  const roundClose = (onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={i18n.t('task_details.close')}
      className="shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark text-kid-text dark:text-kid-text-dark text-2xl font-bold flex items-center justify-center hover:bg-kid-bg dark:hover:bg-kid-bg-dark"
    >
      ×
    </button>
  );

  const stepSegments = (filled: number) => (
    <div className="flex-1 flex gap-2" aria-hidden>
      {checklist.map((item, i) => (
        <span
          key={item.id}
          className={`h-3 md:h-4 flex-1 rounded-full border-2 ${
            i < filled
              ? 'bg-kid-green dark:bg-kid-green-light border-kid-green dark:border-kid-green-light'
              : 'bg-kid-card dark:bg-kid-card-dark border-kid-border dark:border-kid-border-dark'
          }`}
        />
      ))}
    </div>
  );

  const errorBox = flowError && (
    <div role="alert" className="text-base font-semibold text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border-2 border-red-200 dark:border-red-800 rounded-kid-tile p-3">
      {flowError}
    </div>
  );

  // Full-screen flow page: no navigation bar, content kept at a comfortable width on tablets.
  const flowPage = (body: React.ReactNode, tone: 'page' | 'mint' = 'page', fit = false) => (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={liveTask.title}
      className={`fixed inset-0 z-[10050] ${fit ? 'overflow-hidden' : 'overflow-y-auto'} ${tone === 'mint' ? 'bg-kid-green-bg dark:bg-kid-bg-dark' : 'bg-kid-page dark:bg-kid-bg-dark'} text-kid-text dark:text-kid-text-dark`}
    >
      {/* fit: exactly one screen high — only the tool tiles scroll, and only if there are very many */}
      <div className={`${fit ? 'h-full' : 'min-h-full'} w-full max-w-[1180px] mx-auto px-4 md:px-8 py-3 md:py-5 flex flex-col`}>{body}</div>
    </div>
  );

  // A tool tile fills its grid cell: the picture takes the space left above the name, square,
  // capped so one or two tools don't become huge.
  const toolTile = (tool: any) => {
    const label = getToolLabel(tool);
    return (
      <div key={tool.id} className="h-full min-h-0 flex flex-col items-center gap-2 p-2.5 md:p-3 rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark text-center">
        <div className="flex-1 min-h-0 w-full flex items-center justify-center">
          <div className="h-full max-h-[180px] aspect-square max-w-full">
            <PictureTile src={toolUrls[tool.id]} label={label} size="fill" />
          </div>
        </div>
        <span className="shrink-0 font-bold text-base md:text-lg leading-tight line-clamp-2 break-words hyphens-auto" lang={current_language}>{label}</span>
      </div>
    );
  };

  // Tool tiles share the space between the heading and the buttons. Rows grow to fill it and
  // shrink down to minRow px; only below that does this area (and only this area) scroll.
  const toolGrid = (minRow: number) => {
    const cols = toolGridColumns(tools.length, viewportWidth);
    return (
      <div className="flex-1 min-h-0 overflow-y-auto mt-3 md:mt-4 -mx-1 px-1">
        <div
          className="grid gap-3 md:gap-4 h-full justify-center"
          style={{
            gridTemplateColumns: `repeat(${cols}, minmax(0, 260px))`,
            gridAutoRows: `minmax(${minRow}px, 1fr)`,
          }}
        >
          {tools.map((tool) => toolTile(tool))}
        </div>
      </div>
    );
  };

  // ── celebrate ──
  if (phase === 'celebrate') {
    const c = celebrated || { title: liveTask.title, points: liveTask.points };
    return flowPage(
      <div className="flex-1 flex flex-col items-center text-center relative">
        <span className="absolute top-2 left-2 w-5 h-5 rounded bg-kid-orange rotate-12" aria-hidden />
        <span className="absolute top-4 right-4 w-4 h-4 rounded-full bg-kid-green" aria-hidden />
        <span className="absolute top-40 left-3 w-3 h-3 rounded-full bg-[#7FB3F5]" aria-hidden />
        <span className="absolute top-56 right-6 w-4 h-4 rounded bg-kid-family rotate-45" aria-hidden />
        <h2 className="font-kid-heading font-extrabold text-4xl md:text-6xl mt-8 md:mt-12">{i18n.t('task_flow.celebrate_heading')}</h2>
        <AntMascot pose="celebrate" className="w-40 h-40 md:w-56 md:h-56 my-4" />
        <p className="text-xl md:text-2xl font-bold">{i18n.t('task_flow.celebrate_subtitle').replace('{{title}}', c.title)}</p>
        <div className="mt-5 w-full max-w-md flex items-center gap-3 p-4 md:p-5 rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark text-left">
          <Star size={40} />
          <span className="font-kid-heading font-extrabold text-4xl leading-none">{c.points}</span>
          <div>
            {/* A grown-up's own task is approved at once (2026-10-19): the stars are already theirs. */}
            <p className="font-bold text-lg leading-tight">{i18n.t(c.approved ? 'task_flow.celebrate_stars_got_title' : 'task_flow.celebrate_stars_title').replace('{{n}}', String(c.points))}</p>
            <p className="text-base">{i18n.t(c.approved ? 'task_flow.celebrate_stars_got_body' : 'task_flow.celebrate_stars_body')}</p>
          </div>
        </div>
        <div className="mt-auto w-full max-w-xl pt-8 pb-2">
          <KidButton size="xl" fullWidth onClick={leaveToHome}>{i18n.t('task_flow.celebrate_button')}</KidButton>
        </div>
      </div>,
      'mint',
    );
  }

  // ── tools check ──
  if (phase === 'tools_gate') {
    return flowPage(
      <>
        <div className="shrink-0 flex items-center gap-3">
          {roundClose(closeFlow)}
          <p className="flex-1 text-center font-kid-heading font-bold text-lg md:text-xl truncate pr-12 md:pr-14">{liveTask.title}</p>
        </div>
        <div className="shrink-0 flex items-center gap-3 mt-3">
          <AntMascot pose="celebrate" className="w-16 h-16 md:w-24 md:h-24 lg:w-20 lg:h-20 shrink-0" />
          <h2 className="font-kid-heading font-extrabold text-3xl md:text-5xl lg:text-[44px] leading-tight">
            {hasTools ? i18n.t('task_flow.tools_ready_title') : i18n.t('task_flow.ready_to_start_title')}
          </h2>
        </div>
        {/* Where the task is: the room's picture + name (2026-10-13) */}
        {taskRoom && (
          <div className="shrink-0 mt-3 flex items-center gap-3 rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark p-2 pr-4 self-start">
            {taskRoom.image_url && (
              <span className="w-14 h-14 md:w-20 md:h-20 shrink-0"><PictureTile src={taskRoom.image_url} label={taskRoom.name} size="fill" tint="green" rounded="rounded-kid-tile" /></span>
            )}
            <span className="font-bold text-lg md:text-2xl">{i18n.t('task_flow.where_room').replace('{{room}}', taskRoom.name)}</span>
          </div>
        )}
        {hasTools ? toolGrid(150) : (
          <div className="flex-1 min-h-0"><p className="text-xl mt-4">{i18n.t('task_flow.ready_to_start_body')}</p></div>
        )}
        <div className="shrink-0 pt-4 md:pt-5 w-full max-w-xl lg:max-w-3xl mx-auto space-y-2 pb-1">
          {errorBox}
          <div className="flex flex-col lg:flex-row gap-3">
            <KidButton size="xl" fullWidth className="lg:flex-[2]" onClick={startSteps} trailing={<span aria-hidden>→</span>}>
              {i18n.t('task_flow.tools_ready_yes')}
            </KidButton>
            <KidButton size="xl" variant="secondary" fullWidth className="lg:flex-1 !min-h-[56px] lg:!min-h-[80px]" onClick={closeFlow}>
              {i18n.t('task_flow.tools_ready_no')}
            </KidButton>
          </div>
          <button type="button" onClick={() => setPhase('view')} className="block mx-auto min-h-[44px] px-3 font-bold underline text-kid-green-shadow dark:text-green-300">
            {i18n.t('task_flow.see_details')}
          </button>
        </div>
      </>,
      'page',
      true,
    );
  }

  // ── tidy up ──
  const renderTidyUp = () =>
    flowPage(
      <>
        <div className="shrink-0 flex items-center gap-3">
          {roundClose(closeFlow)}
          {stepSegments(checklist.length)}
        </div>
        {/* Tidy-up (2026-11-01, Jens): the general "put back" / "towels in the basket" cards didn't fit
            every task, so it shows the task's own tools as pictures with one line to put them back. */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center text-center mt-3">
          <AntMascot pose="celebrate" className="w-20 h-20 md:w-28 md:h-28 shrink-0" />
          <h2 className="font-kid-heading font-extrabold text-4xl md:text-5xl mt-2">{i18n.t('task_flow.tidy_title')}</h2>
          {hasTools ? (
            <>
              <p className="mt-3 font-bold text-xl md:text-2xl max-w-xl">{i18n.t('task_flow.tidy_put_all_back')}</p>
              <ul className="mt-4 w-full max-w-xl grid grid-cols-2 sm:grid-cols-3 gap-3">
                {tools.map((tool) => (
                  <li key={tool.id} className="flex flex-col items-center gap-2 rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark p-2">
                    <span className="w-full aspect-square"><PictureTile src={toolUrls[tool.id]} label={getToolLabel(tool)} size="fill" /></span>
                    <span className="font-bold text-base" lang={current_language}>{getToolLabel(tool)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="mt-3 font-bold text-xl md:text-2xl max-w-xl">{i18n.t('task_flow.tidy_no_tools')}</p>
          )}
        </div>
        <div className="shrink-0 pt-4 md:pt-5 w-full max-w-xl mx-auto space-y-2 pb-1">
          {errorBox}
          <KidButton size="xl" fullWidth disabled={completeBusy} onClick={handleCompleteTask} icon={<span aria-hidden>✓</span>}>
            {i18n.t('task_flow.complete_task_button')}
          </KidButton>
          <button
            type="button"
            disabled={completeBusy}
            onClick={() => { unmarkStep(checklist.length - 1); setViewIndex(checklist.length - 1); setPhase('doing'); }}
            className="block mx-auto min-h-[44px] px-3 font-bold underline text-kid-green-shadow dark:text-green-300 disabled:opacity-50"
          >
            {i18n.t('task_flow.back_to_steps')}
          </button>
        </div>
      </>,
      'mint',
      true,
    );

  // While completing, the family reload briefly marks the task completed; stay on the tidy-up
  // screen (the review's "flash" bug) until the celebration takes over.
  if (phase === 'final' && (isMine || completeBusy)) return renderTidyUp();

  // ── one step ──
  if (phase === 'doing') {
    const index = stepToShow(doneFlags, viewIndex);
    if (index >= checklist.length) return renderTidyUp();
    const item = checklist[index];
    const imgs = checklistUrls[item.id] || [];
    return flowPage(
      <>
        <div className="shrink-0 flex items-center gap-3">
          {roundClose(closeFlow)}
          {stepSegments(index)}
        </div>
        <div className="flex-1 flex flex-col lg:grid lg:grid-cols-2 lg:gap-10 mt-3 lg:mt-6">
          <div className="flex flex-col">
            <p className="text-center lg:hidden font-bold text-base mb-2">
              {i18n.t('task_flow.step_of').replace('{{n}}', String(index + 1)).replace('{{total}}', String(checklist.length))}
            </p>
            <div className="w-full aspect-[4/3] lg:aspect-auto lg:flex-1 lg:min-h-[420px] rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark overflow-hidden">
              {imgs.length > 0 ? (
                <div className={`w-full h-full grid gap-2 p-2 ${imgs.length > 1 ? 'grid-cols-2' : ''} bg-kid-tint-blue`}>
                  {imgs.map((u, i) => (
                    <PictureTile key={i} src={u} label={item.text} size="fill" tint="blue" rounded="rounded-kid-tile" />
                  ))}
                </div>
              ) : (
                <PictureTile src={null} label={item.text} size="fill" rounded="" />
              )}
            </div>
          </div>
          <div className="flex flex-col">
            <p className="hidden lg:block font-bold text-xl">
              {i18n.t('task_flow.step_of').replace('{{n}}', String(index + 1)).replace('{{total}}', String(checklist.length))}
            </p>
            <h2 className="font-kid-heading font-extrabold text-3xl md:text-5xl lg:text-6xl leading-tight text-center lg:text-left mt-4 lg:mt-2">
              {item.text}
            </h2>
            {/* Tools for this step (2026-10-13): small picture + name, so the child knows what to grab. */}
            {(() => {
              const stepTools = ((item as any).tool_ids || [])
                .map((id: string) => current_family.tools.find((x: any) => x.id === id))
                .filter(Boolean) as any[];
              if (!stepTools.length) return null;
              return (
                <div className="mt-4">
                  <p className="font-bold text-lg text-center lg:text-left">{i18n.t('task_flow.step_tools')}</p>
                  <ul className="mt-2 flex flex-wrap justify-center lg:justify-start gap-2">
                    {stepTools.map((tool) => (
                      <li key={tool.id} className="flex items-center gap-2 rounded-full border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark pl-1 pr-4 py-1">
                        <span className="w-11 h-11 shrink-0"><PictureTile src={toolUrls[tool.id]} label={getToolLabel(tool)} size="fill" rounded="rounded-full" /></span>
                        <span className="font-bold text-base md:text-lg" lang={current_language}>{getToolLabel(tool)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            {/* Timed step (2026-10-19): start it when ready; ticking the step stays a separate tap. */}
            {(item as any).timer_seconds > 0 && (
              <StepTimer key={item.id} seconds={(item as any).timer_seconds} personId={current_user.id} />
            )}
            <div className="mt-auto pt-6 space-y-3">
              {errorBox}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleBack(index)}
                  disabled={index === 0}
                  aria-label={i18n.t('task_flow.back_step')}
                  title={i18n.t('task_flow.back_step')}
                  className="shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full bg-kid-chip dark:bg-kid-chip-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-2xl font-bold flex items-center justify-center disabled:opacity-30"
                >
                  <span aria-hidden>←</span>
                </button>
                <KidButton size="xl" fullWidth onClick={() => handleDidIt(index)} icon={<span aria-hidden>✓</span>}>
                  {i18n.t('task_flow.finish_step')}
                </KidButton>
              </div>
            </div>
          </div>
        </div>
      </>,
    );
  }

  // ── view (details sheet) ── restyled in rollout step 5
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10050] p-4" role="dialog" aria-modal="true" aria-label={liveTask.title}>
      <div className="bg-kid-bg dark:bg-kid-bg-dark rounded-kid-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between gap-3 p-4">
          <h2 className="font-kid-heading font-bold text-xl text-kid-text dark:text-kid-text-dark pr-2">{liveTask.title}</h2>
          {roundClose(onClose)}
        </div>
        <div className="px-6 pb-6 space-y-6 text-kid-text dark:text-kid-text-dark">
          {headerSrc && (
            <div className="w-full h-48 rounded-kid-lg overflow-hidden">
              <PictureTile src={headerSrc} label={liveTask.title} size="fill" rounded="rounded-kid-lg" fit="cover" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="font-medium text-kid-text-muted dark:text-kid-text-muted-dark">{i18n.t('task_details.room')}</span>
              <p className="flex items-center gap-2">
                {taskRoom?.image_url && <span className="w-12 h-12 shrink-0"><PictureTile src={taskRoom.image_url} label={taskRoom.name} size="fill" tint="green" rounded="rounded-kid-tile" /></span>}
                {getRoomName((liveTask as any).room_id)}
              </p>
            </div>
            <div>
              <span className="font-medium text-kid-text-muted dark:text-kid-text-muted-dark">{i18n.t('task_details.points')}</span>
              <p className="flex items-center gap-1"><Star size={16} /> {liveTask.points}</p>
            </div>
            {(liveTask as any).due_date && (
              <div className="col-span-2">
                <span className="font-medium text-kid-text-muted dark:text-kid-text-muted-dark">{i18n.t('task_details.due_date')}</span>
                <p>📅 {new Date((liveTask as any).due_date).toLocaleDateString()}</p>
              </div>
            )}
            {!isCompleted && (
              <div className="col-span-2">
                <span className="font-medium text-kid-text-muted dark:text-kid-text-muted-dark">{i18n.t('task_details.assigned_to')}</span>
                <p>{assigneeName || i18n.t('task_flow.not_assigned')}</p>
              </div>
            )}
          </div>

          {liveTask.description && <p className="text-kid-text-muted dark:text-kid-text-muted-dark text-sm">{liveTask.description}</p>}

          {isCompleted && (
            <div className="bg-kid-green-bg dark:bg-green-900/20 p-4 rounded-kid-lg">
              <p className="font-semibold text-kid-text dark:text-green-200">{i18n.t('task_details.task_completed')}</p>
            </div>
          )}

          {hasTools && (
            <div>
              <h4 className="text-sm font-semibold text-kid-text-muted dark:text-kid-text-muted-dark mb-3">{i18n.t('task_details.tools_required')}</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {tools.map((tool) => (
                  <div key={tool.id} className="flex flex-col items-center text-center p-3 rounded-2xl bg-kid-green-bg dark:bg-gray-900/50">
                    <PictureTile src={toolUrls[tool.id]} label={getToolLabel(tool)} size={64} />
                    <span className="mt-2 text-sm font-medium leading-tight">{getToolLabel(tool)}</span>
                    {getToolDesc(tool) && (
                      <span className="text-xs text-kid-text-muted dark:text-kid-text-muted-dark mt-1 line-clamp-2">{getToolDesc(tool)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasChecklist && (
            <div>
              <h4 className="font-kid-heading font-bold text-lg mb-2">{i18n.t('task_details.checklist')}</h4>
              <ol className="space-y-2">
                {checklist.map((item, index) => {
                  const done = isDone(item.id);
                  return (
                    <li key={item.id} className={`flex gap-3 p-3 rounded-2xl ${done ? 'bg-kid-green-bg dark:bg-green-900/20' : 'bg-white/60 dark:bg-gray-900/40'}`}>
                      <span className="text-base font-bold shrink-0 w-6 text-center" aria-hidden>{done ? '✓' : index + 1}</span>
                      <p className={`text-sm flex-1 ${done ? 'line-through text-kid-text-muted dark:text-kid-text-muted-dark' : ''}`}>{item.text}</p>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {!hasChecklist && !isCompleted && (
            <p className="text-sm text-amber-800 dark:text-amber-300 bg-kid-orange-bg dark:bg-amber-950/30 rounded-kid-lg p-3">
              {i18n.t('task_flow.no_checklist_warning')}
            </p>
          )}

          <div className="flex flex-col gap-3 pt-2">
            {isMine && !isCompleted && hasChecklist && (
              <KidButton size="lg" fullWidth onClick={handleStartTask} trailing={<span aria-hidden>→</span>}>
                {i18n.t('task_flow.start_task')}
              </KidButton>
            )}

            {!(liveTask as any).assigned_to && (
              <KidButton
                size="lg"
                fullWidth
                onClick={async () => {
                  try {
                    await claimTask(liveTask.id);
                    await refreshFamilyData();
                  } catch (e) {
                    console.error(e);
                  }
                }}
              >
                {i18n.t('task_board.take_task')}
              </KidButton>
            )}

            {isMine && !isCompleted && (
              <KidButton
                size="md"
                variant="secondary"
                fullWidth
                onClick={async () => {
                  try {
                    await unclaimTask(liveTask.id);
                    await refreshFamilyData();
                    onClose();
                  } catch (e) {
                    console.error(e);
                  }
                }}
              >
                {i18n.t('task_board.unclaim_task')}
              </KidButton>
            )}

            {(liveTask as any).assigned_to &&
              (liveTask as any).assigned_to !== current_user.id &&
              !isCompleted &&
              current_user.is_admin && (
                <KidButton
                  size="md"
                  variant="secondary"
                  fullWidth
                  onClick={async () => {
                    try {
                      await claimTask(liveTask.id);
                      await refreshFamilyData();
                    } catch (e) {
                      console.error(e);
                    }
                  }}
                >
                  {i18n.t('task_board.take_over_task')}
                </KidButton>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskMemberModal;
